import React from 'react';
import classNames from 'classnames';
import { generatePath, Link } from 'react-router-dom';
import {
  AvailableIcons,
  Icon,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import { getProductName } from 'src/components-bl/MerchandisingRules/merchandisingRules.helpers';
import { allSyteProducts } from 'src/components-bl/shared/syte-products-dropdown-options';
import { SyteProductType } from 'src/services/src/service/types/constants/syte-product-types';
import { useStepControlsSync } from '../useStepControlsSync';
import {
  LEVER_OPTIONS,
  LEVER_LABEL,
  RISK_LABEL,
  formatDataFieldsNote,
  getCompactMetricLabel,
} from './constants';
import { useWhatToTestStep } from './useWhatToTestStep';
import { WhatToTestStepProps } from './types';
import './WhatToTestStep.scss';

export const WhatToTestStep = (props: WhatToTestStepProps): JSX.Element => {
  const { shopId, dataFieldsRoute, onStepControlsChange } = props;
  const {
    mode,
    setMode,
    playbooks,
    isLoadingPlaybooks,
    selectedPlaybookId,
    selectPlaybook,
    selectedSurface,
    selectSurface,
    selectedLever,
    selectLever,
    hasMerchandisingRules,
    canContinue,
    isSubmitting,
    onSubmit,
  } = useWhatToTestStep(props);

  useStepControlsSync({ canContinue, isSubmitting, onSubmit }, onStepControlsChange, [
    canContinue,
    isSubmitting,
    onSubmit,
  ]);

  const dataFieldsPath = dataFieldsRoute ? generatePath(dataFieldsRoute, { shopId }) : undefined;

  return (
    <div className='syte-what-to-test-step'>
      <Typography type={TypographyType.Heading} variant={TypographyVariant.MediumBold}>
        What do you want to test?
      </Typography>
      <Typography
        type={TypographyType.Body}
        variant={TypographyVariant.SmallRegular}
        className='syte-what-to-test-step-subtitle'
      >
        Start from a playbook — a known experiment with a defined lever and a ready-made variant —
        or build your own from a surface and a lever.
      </Typography>

      <div className='syte-what-to-test-step-tabs'>
        <button
          type='button'
          className={classNames('syte-what-to-test-step-tab', {
            'syte-what-to-test-step-tab-selected': mode === 'playbooks',
          })}
          onClick={() => setMode('playbooks')}
        >
          Playbooks
        </button>
        <button
          type='button'
          className={classNames('syte-what-to-test-step-tab', {
            'syte-what-to-test-step-tab-selected': mode === 'build-your-own',
          })}
          onClick={() => setMode('build-your-own')}
        >
          Build your own
        </button>
      </div>

      {mode === 'playbooks' && (
        <div className='syte-what-to-test-step-playbooks'>
          {isLoadingPlaybooks && (
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Loading playbooks...
            </Typography>
          )}

          {!isLoadingPlaybooks && (
            <div className='syte-what-to-test-step-playbook-cards'>
              {playbooks.map(playbook => (
                <button
                  key={playbook.id}
                  type='button'
                  disabled={!playbook.satisfied}
                  className={classNames('syte-what-to-test-step-playbook-card', {
                    'syte-what-to-test-step-playbook-card-selected':
                      selectedPlaybookId === playbook.id,
                    'syte-what-to-test-step-playbook-card-disabled': !playbook.satisfied,
                  })}
                  onClick={() => selectPlaybook(playbook.id)}
                >
                  <div className='syte-what-to-test-step-playbook-card-header'>
                    <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                      {playbook.name}
                    </Typography>
                    <span
                      className={classNames(
                        'syte-what-to-test-step-risk-badge',
                        `syte-what-to-test-step-risk-badge-${playbook.risk}`
                      )}
                    >
                      {RISK_LABEL[playbook.risk]}
                    </span>
                  </div>
                  <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                    {playbook.description}
                  </Typography>
                  <div className='syte-what-to-test-step-playbook-card-meta'>
                    <span className='syte-what-to-test-step-playbook-card-meta-item'>
                      <Icon name={AvailableIcons.DottedCircle} />
                      <Typography
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                      >
                        {getProductName(playbook.surface as SyteProductType)}
                      </Typography>
                    </span>
                    <span className='syte-what-to-test-step-playbook-card-meta-item'>
                      <Icon name={AvailableIcons.Gear} />
                      <Typography
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                      >
                        {getCompactMetricLabel(playbook.metric)}
                      </Typography>
                    </span>
                    <span className='syte-what-to-test-step-playbook-card-meta-item'>
                      <Icon name={AvailableIcons.Sorting} />
                      <Typography
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                      >
                        {LEVER_LABEL[playbook.leverType]}
                      </Typography>
                    </span>
                    <span className='syte-what-to-test-step-playbook-card-meta-item'>
                      <Icon name={AvailableIcons.Calendar} />
                      <Typography
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                      >
                        ~{playbook.recommendedDurationDays} days
                      </Typography>
                    </span>
                  </div>
                  {playbook.satisfied && playbook.requiredDataFields.length > 0 && (
                    <div className='syte-what-to-test-step-playbook-card-note'>
                      <Typography
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                      >
                        {formatDataFieldsNote(playbook.requiredDataFields)} — found in your
                        catalogue.
                      </Typography>
                    </div>
                  )}
                  {!playbook.satisfied && (
                    <div className='syte-what-to-test-step-playbook-card-note syte-what-to-test-step-playbook-card-note-warning'>
                      <Typography
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                      >
                        {formatDataFieldsNote(playbook.missingFields)} — not found in your
                        catalogue.
                        {dataFieldsPath && (
                          <>
                            {' '}
                            <Link to={dataFieldsPath} onClick={event => event.stopPropagation()}>
                              Add them in Catalogue Manager
                            </Link>
                          </>
                        )}
                      </Typography>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'build-your-own' && (
        <div className='syte-what-to-test-step-build-your-own'>
          <div className='syte-what-to-test-step-panel'>
            <Typography
              type={TypographyType.Body}
              variant={TypographyVariant.SmallRegular}
              className='syte-what-to-test-step-section-label'
            >
              <span className='syte-what-to-test-step-section-label-number'>1</span>
              {' · '}WHERE DOES THE CHANGE APPLY?
            </Typography>
            <div className='syte-what-to-test-step-surfaces'>
              {allSyteProducts.map(surface => (
                <button
                  key={surface}
                  type='button'
                  className={classNames('syte-what-to-test-step-surface', {
                    'syte-what-to-test-step-surface-selected': selectedSurface === surface,
                  })}
                  onClick={() => selectSurface(surface)}
                >
                  {getProductName(surface)}
                </button>
              ))}
            </div>

            <Typography
              type={TypographyType.Body}
              variant={TypographyVariant.SmallRegular}
              className='syte-what-to-test-step-section-label'
            >
              <span className='syte-what-to-test-step-section-label-number'>2</span>
              {' · '}WHICH LEVER ARE YOU TESTING?
            </Typography>
            <div className='syte-what-to-test-step-levers'>
              {LEVER_OPTIONS.map(({ leverType, label, description }) => {
                const isDisabled =
                  leverType === 'merchandising_rule' && hasMerchandisingRules === false;
                return (
                  <button
                    key={leverType}
                    type='button'
                    disabled={isDisabled}
                    className={classNames('syte-what-to-test-step-lever-card', {
                      'syte-what-to-test-step-lever-card-selected': selectedLever === leverType,
                      'syte-what-to-test-step-lever-card-disabled': isDisabled,
                    })}
                    onClick={() => selectLever(leverType)}
                  >
                    <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                      {label}
                    </Typography>
                    <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                      {description}
                    </Typography>
                    {isDisabled && (
                      <Typography
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                        className='syte-what-to-test-step-lever-card-note'
                      >
                        This shop has no merchandising rules yet.
                      </Typography>
                    )}
                  </button>
                );
              })}
            </div>

            <div className='syte-what-to-test-step-warning'>
              <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                <span className='syte-what-to-test-step-warning-bold'>
                  One lever per experiment.
                </span>{' '}
                You can add a second change later, but we&apos;ll flag it — with two changes at once
                you won&apos;t know which one moved the number.
              </Typography>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
